import { useQuery } from '@tanstack/react-query';
import { useRoute, Link } from 'wouter';
import { useState, type ReactNode } from 'react';
import { endpoints } from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { StatusBadge, StatusIcon } from '@/components/ui/StatusBadge';
import {
  ArrowLeft,
  ExternalLink,
  Server,
  Activity,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { formatTimeAgo, formatUptime, formatClock } from '@/lib/format';
import { useNow } from '@/lib/hooks';
import { cn } from '@/lib/utils';
import { AdGuardWidget } from '@/components/AdGuardWidget';
import { QBittorrentWidget } from '@/components/QBittorrentWidget';
import { JellyfinWidget } from '@/components/JellyfinWidget';
import { GiteaWidget } from '@/components/GiteaWidget';
import { PelicanWidget } from '@/components/PelicanWidget';
import type { ServiceId } from '@shared/services';

const REFETCH = 5_000;

const INTEGRATION_WIDGETS: Partial<Record<ServiceId, () => ReactNode>> = {
  adguard: () => <AdGuardWidget />,
  qbittorrent: () => <QBittorrentWidget />,
  jellyfin: () => <JellyfinWidget />,
  gitea: () => <GiteaWidget />,
  'pelican-panel': () => <PelicanWidget />,
  'pelican-wings': () => <PelicanWidget />,
};

export function ServiceDetailRoute() {
  const [, params] = useRoute<{ id: string }>('/services/:id');
  const id = params?.id as ServiceId | undefined;
  const now = useNow(1_000);

  const q = useQuery({
    queryKey: ['service', id],
    queryFn: () => endpoints.services.one(id as ServiceId),
    enabled: !!id,
    refetchInterval: REFETCH,
  });
  const eventsQ = useQuery({
    queryKey: ['events', 'service', id, 50],
    queryFn: () => endpoints.events.list(50),
    enabled: !!id,
    refetchInterval: REFETCH,
  });
  const svcEvents = (eventsQ.data?.events ?? []).filter((e) => e.subject === id);

  const [historyWindow, setHistoryWindow] = useState<'24h' | '7d'>('24h');
  const historyQ = useQuery({
    queryKey: ['service-history', id, historyWindow],
    queryFn: () => endpoints.serviceHistory(id as ServiceId, historyWindow),
    enabled: !!id,
    refetchInterval: REFETCH * 2,
  });

  if (!id) return null;

  const IntegrationWidget = INTEGRATION_WIDGETS[id];

  return (
    <div className="space-y-6">
      <Link href="/services">
        <a className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
          <ArrowLeft className="h-3.5 w-3.5" />
          all services
        </a>
      </Link>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <StatusIcon status={q.data?.health.status ?? 'unknown'} />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {q.data?.displayName ?? id}
            </h1>
            <p className="text-xs text-muted-foreground font-mono">
              {id} · vmid {q.data?.vmid} · {q.data?.ip}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={q.data?.health.status ?? 'unknown'} />
          {q.data?.webUrl && (
            <a
              href={q.data.webUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground border border-border rounded-md px-2 py-1"
            >
              open web UI <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Health</CardTitle>
            {q.data?.health.httpCheckedAt && (
              <span className="text-xs text-muted-foreground">
                probed {formatTimeAgo(q.data.health.httpCheckedAt, now)}
              </span>
            )}
          </CardHeader>
          <CardBody>
            {q.data ? (
              <div className="space-y-2 text-sm">
                <Row label="LXC status" value={q.data.health.status} />
                <Row label="LXC check" value={formatTimeAgo(q.data.health.lxcCheckedAt, now)} />
                <Row label="HTTP check" value={formatTimeAgo(q.data.health.httpCheckedAt, now)} />
                {q.data.health.lastError && (
                  <div className="rounded-md border border-rose-500/30 bg-rose-500/5 p-2 text-xs text-rose-300 font-mono">
                    {q.data.health.lastError}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">loading…</div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-3.5 w-3.5" />
              Service events
            </CardTitle>
            <span className="text-xs text-muted-foreground">{svcEvents.length}</span>
          </CardHeader>
          <CardBody>
            {svcEvents.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                no events recorded for this service yet
              </div>
            ) : (
              <ul className="space-y-1.5 text-xs max-h-64 overflow-y-auto">
                {svcEvents.map((e) => (
                  <li key={e.id} className="flex items-start gap-2">
                    <span
                      className={cn(
                        'inline-block h-1.5 w-1.5 rounded-full mt-1.5 shrink-0',
                        e.level === 'error' && 'bg-rose-500',
                        e.level === 'warn' && 'bg-amber-500',
                        e.level === 'info' && 'bg-sky-500',
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-foreground/80 truncate">{e.message}</div>
                      <div className="text-muted-foreground/70 text-[10px]">
                        {formatTimeAgo(e.timestamp, now)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-3.5 w-3.5" />
            Health history
          </CardTitle>
          <div className="flex items-center gap-1">
            {(['24h', '7d'] as const).map((w) => (
              <button
                key={w}
                onClick={() => setHistoryWindow(w)}
                className={cn(
                  'px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider transition-colors',
                  historyWindow === w
                    ? 'bg-accent text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {w}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardBody>
          <HistoryTimeline
            loading={historyQ.isLoading}
            currentState={historyQ.data?.currentState ?? q.data?.health.status ?? 'unknown'}
            currentSince={historyQ.data?.currentSince ?? null}
            transitionCount={historyQ.data?.transitionCount ?? 0}
            events={historyQ.data?.events ?? []}
            now={now}
          />
        </CardBody>
      </Card>

      {IntegrationWidget && (
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-2">
            <Server className="h-3.5 w-3.5" />
            Live data
          </h2>
          <IntegrationWidget />
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}

function HistoryTimeline({
  loading,
  currentState,
  currentSince,
  transitionCount,
  events,
  now,
}: {
  loading: boolean;
  currentState: 'healthy' | 'unhealthy' | 'unknown';
  currentSince: string | null;
  transitionCount: number;
  events: Array<{
    id: number;
    timestamp: string;
    level: 'info' | 'warn' | 'error';
    from: string | null;
    to: string | null;
    lastError: string | null;
  }>;
  now: number;
}) {
  if (loading) {
    return <div className="text-sm text-muted-foreground">loading history…</div>;
  }

  const currentSinceMs = currentSince ? Date.parse(currentSince) : null;
  const currentDurationSec =
    currentSinceMs !== null && Number.isFinite(currentSinceMs)
      ? Math.max(0, Math.floor((now - currentSinceMs) / 1000))
      : 0;
  const isUp = currentState === 'healthy';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'inline-block h-2 w-2 rounded-full',
              isUp ? 'bg-emerald-500' : currentState === 'unhealthy' ? 'bg-rose-500' : 'bg-muted-foreground',
            )}
          />
          <span className="font-medium">
            {currentState === 'healthy' ? 'up' : currentState === 'unhealthy' ? 'down' : 'unknown'}
          </span>
          {currentSince && (
            <span className="text-muted-foreground text-xs">
              for {formatUptime(currentDurationSec)} · since {formatClock(currentSince)}
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          {transitionCount === 0
            ? 'no transitions in window'
            : `${transitionCount} transition${transitionCount === 1 ? '' : 's'}`}
        </div>
      </div>

      {events.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground">
          No state changes in this window. If a service has been up the whole time,
          that's a good thing.
        </div>
      ) : (
        <ol className="space-y-1.5 max-h-96 overflow-y-auto">
          {events
            .slice()
            .reverse()
            .map((e, i, arr) => {
              const ts = Date.parse(e.timestamp);
              const next = arr[i + 1];
              const nextTs = next ? Date.parse(next.timestamp) : null;
              // Duration of the *previous* state — i.e. how long the
              // service sat in `from` before flipping to `to`. For
              // the oldest event we don't have a "next" to look at.
              const durationSec =
                ts && nextTs && Number.isFinite(ts) && Number.isFinite(nextTs)
                  ? Math.max(0, Math.floor((nextTs - ts) / 1000))
                  : null;
              const up =
                e.to === 'healthy' || e.to === 'online' || e.to === 'running';
              const down =
                e.to === 'unhealthy' || e.to === 'offline' || e.to === 'stopped';
              return (
                <li
                  key={e.id}
                  className="flex items-start gap-2.5 text-xs border-b border-border/40 pb-1.5 last:border-b-0"
                >
                  <span
                    className={cn(
                      'mt-1 inline-block h-1.5 w-1.5 rounded-full shrink-0',
                      e.level === 'error' && 'bg-rose-500',
                      e.level === 'warn' && 'bg-amber-500',
                      e.level === 'info' && 'bg-sky-500',
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-muted-foreground">{e.from ?? '?'}</span>
                      <span className="text-muted-foreground/50">→</span>
                      <span
                        className={cn(
                          'font-medium',
                          up && 'text-emerald-300',
                          down && 'text-rose-300',
                        )}
                      >
                        {e.to ?? '?'}
                      </span>
                      {e.from === 'unhealthy' && (
                        <TrendingUp className="h-3 w-3 text-emerald-400" />
                      )}
                      {e.from === 'healthy' && (
                        <TrendingDown className="h-3 w-3 text-rose-400" />
                      )}
                      {durationSec !== null && (
                        <span className="text-muted-foreground/70 text-[10px]">
                          ({formatUptime(durationSec)} in previous state)
                        </span>
                      )}
                    </div>
                    {e.lastError && (
                      <div className="text-rose-300/80 text-[10px] font-mono truncate mt-0.5">
                        {e.lastError}
                      </div>
                    )}
                  </div>
                  <div className="text-muted-foreground/70 text-[10px] tabular-nums shrink-0">
                    {formatTimeAgo(e.timestamp, now)}
                  </div>
                </li>
              );
            })}
        </ol>
      )}
    </div>
  );
}
